<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import AppIcon from './AppIcon.vue'
import { ApiError, apiRequest } from '../services/api'
import { BLUFF_PACK_FORMAT } from '../../shared/contracts/bluff'

interface PackSummary { id: string; name: string; description: string | null; archived: number; question_count: number }
interface Settings { bluffSeconds: number | null; voteSeconds: number | null; truthPoints: number; foolPoints: number; profanityFilterEnabled: boolean; maxTeamSize: number | null }
interface Question { id?: string; prompt: string; truth: string; aliases: string[]; explanation: string; sourceName: string; sourceUrl: string | null; decoys: string[]; active: boolean }
interface Pack { id: string; name: string; description: string | null; archived: boolean; settings: Settings; questions: Question[] }
interface Draft { prompt: string; truth: string; aliases: string; explanation: string; sourceName: string; sourceUrl: string; decoys: string; active: boolean }

const router = useRouter()
const packs = ref<PackSummary[]>([])
const pack = ref<Pack | null>(null)
const drafts = ref<Record<string, Draft>>({})
const newQuestion = ref<Draft>(emptyDraft())
const newPackName = ref('')
const importInput = ref<HTMLInputElement | null>(null)
const error = ref('')
const notice = ref('')
const busy = ref(false)

function emptyDraft(): Draft {
  return { prompt: '', truth: '', aliases: '', explanation: '', sourceName: '', sourceUrl: '', decoys: '', active: true }
}

function toDraft(question: Question): Draft {
  return { ...question, aliases: question.aliases.join(', '), decoys: question.decoys.join(', '), sourceUrl: question.sourceUrl ?? '' }
}

function fromDraft(draft: Draft): Omit<Question, 'id'> {
  const list = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)
  return { prompt: draft.prompt, truth: draft.truth, aliases: list(draft.aliases), explanation: draft.explanation, sourceName: draft.sourceName,
    sourceUrl: draft.sourceUrl.trim() || null, decoys: list(draft.decoys), active: draft.active }
}

async function run(action: () => Promise<void>, success = ''): Promise<void> {
  error.value = ''; notice.value = ''; busy.value = true
  try { await action(); notice.value = success } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'The request could not be completed.'
  } finally { busy.value = false }
}

async function loadPacks(): Promise<void> {
  packs.value = (await apiRequest<{ packs: PackSummary[] }>('/api/admin/bluff/packs')).packs
}

async function openPack(id: string): Promise<void> {
  pack.value = (await apiRequest<{ pack: Pack }>(`/api/admin/bluff/packs/${id}`)).pack
  drafts.value = Object.fromEntries(pack.value.questions.map((question) => [question.id!, toDraft(question)]))
}

onMounted(() => run(loadPacks))

const createPack = () => run(async () => {
  const result = await apiRequest<{ id: string }>('/api/admin/bluff/packs', { method: 'POST', body: JSON.stringify({ name: newPackName.value }) })
  newPackName.value = ''
  await loadPacks(); await openPack(result.id)
})

const addDemo = () => run(async () => {
  const result = await apiRequest<{ id: string }>('/api/admin/bluff/packs/demo', { method: 'POST', body: '{}' })
  await loadPacks(); await openPack(result.id)
}, 'Demo pack added.')

async function importFile(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return
  await run(async () => {
    let document: { format?: string }
    try { document = JSON.parse(await file.text()) as { format?: string } } catch { throw new ApiError('INVALID_FILE', 'That file is not valid JSON.', 400) }
    if (document.format !== BLUFF_PACK_FORMAT) throw new ApiError('INVALID_FILE', 'That file is not a Bar Bluff question pack.', 400)
    const result = await apiRequest<{ id: string }>('/api/admin/bluff/packs/import', { method: 'POST', body: JSON.stringify(document) })
    await loadPacks(); await openPack(result.id)
  }, 'Pack imported.')
  if (importInput.value) importInput.value.value = ''
}

const savePack = () => run(async () => {
  if (!pack.value) return
  await apiRequest(`/api/admin/bluff/packs/${pack.value.id}`, { method: 'PUT', body: JSON.stringify({
    name: pack.value.name, description: pack.value.description || null, settings: pack.value.settings }) })
  await loadPacks()
}, 'Pack saved. Live games keep the rules they started with.')

const archivePack = (id: string) => run(async () => {
  if (!window.confirm('Archive this pack? It can no longer be hosted.')) return
  await apiRequest(`/api/admin/bluff/packs/${id}/archive`, { method: 'POST', body: '{}' })
  await loadPacks()
  if (pack.value?.id === id) await openPack(id)
})

const saveQuestion = (id: string) => run(async () => {
  await apiRequest(`/api/admin/bluff/packs/${pack.value!.id}/questions/${id}`, { method: 'PUT', body: JSON.stringify(fromDraft(drafts.value[id]!)) })
  await openPack(pack.value!.id)
}, 'Question saved.')

const addQuestion = () => run(async () => {
  await apiRequest(`/api/admin/bluff/packs/${pack.value!.id}/questions`, { method: 'POST', body: JSON.stringify(fromDraft(newQuestion.value)) })
  newQuestion.value = emptyDraft()
  await openPack(pack.value!.id); await loadPacks()
}, 'Question added.')

const deleteQuestion = (id: string) => run(async () => {
  if (!window.confirm('Delete this question?')) return
  await apiRequest(`/api/admin/bluff/packs/${pack.value!.id}/questions/${id}`, { method: 'DELETE' })
  await openPack(pack.value!.id); await loadPacks()
})

const move = (index: number, offset: number) => run(async () => {
  const ids = pack.value!.questions.map((question) => question.id!)
  const [moved] = ids.splice(index, 1)
  ids.splice(index + offset, 0, moved!)
  await apiRequest(`/api/admin/bluff/packs/${pack.value!.id}/questions/reorder`, { method: 'POST', body: JSON.stringify({ orderedIds: ids }) })
  await openPack(pack.value!.id)
})

const host = (id: string) => run(async () => {
  const session = await apiRequest<{ sessionId: string }>(`/api/admin/bluff/packs/${id}/sessions`, { method: 'POST', body: '{}' })
  await router.push(`/host/${session.sessionId}`)
})

function timer(value: string): number | null {
  return value === '' ? null : Number(value)
}
</script>

<template>
  <section
    v-reveal
    class="panel panel--dark mt-24 stack stack--lg"
  >
    <div class="panel__intro">
      <h2 class="text-xxl fw-6">
        Bar Bluff question packs
      </h2>
      <button
        type="button"
        class="btn--secondary btn--sm"
        :disabled="busy"
        @click="addDemo"
      >
        <AppIcon
          name="sparkles"
          :size="18"
        /> Add demo pack
      </button>
    </div>
    <p class="muted-text">
      Each question needs a verified fact, its true answer, an explanation and a source. Teams invent fake answers, then try to spot the truth.
    </p>
    <p
      v-if="error"
      class="alert"
      role="alert"
    >
      {{ error }}
    </p>
    <p
      v-if="notice"
      class="notice"
      role="status"
    >
      {{ notice }}
    </p>

    <div class="admin-grid">
      <aside class="stack">
        <form
          class="inline-form"
          @submit.prevent="createPack"
        >
          <div class="input-single input-single--soft">
            <AppIcon name="plus" />
            <input
              v-model="newPackName"
              minlength="2"
              maxlength="120"
              aria-label="New pack name"
              placeholder="New pack name"
            >
          </div>
          <button
            type="submit"
            class="btn--primary btn--sm"
            :disabled="busy || newPackName.trim().length < 2"
          >
            Create pack
          </button>
        </form>
        <div class="input-wrapper">
          <label for="pack-import">Import a pack file</label>
          <div class="input-single input-single--soft">
            <AppIcon name="upload" />
            <input
              id="pack-import"
              ref="importInput"
              type="file"
              accept="application/json,.json"
              @change="importFile"
            >
          </div>
        </div>
        <p
          v-if="!packs.length"
          class="muted-text"
        >
          No packs yet. Add the demo pack to try a game.
        </p>
        <article
          v-for="item in packs"
          :key="item.id"
          class="list-card"
          :class="{ muted: item.archived }"
        >
          <button
            class="text-button"
            type="button"
            @click="run(() => openPack(item.id))"
          >
            {{ item.name }}
          </button>
          <span class="text-sm muted-text">{{ item.question_count }} active questions<template v-if="item.archived"> · archived</template></span>
          <div class="button-row">
            <button
              v-if="!item.archived"
              type="button"
              class="btn--primary btn--sm"
              :disabled="busy || !item.question_count"
              @click="host(item.id)"
            >
              <AppIcon
                name="play"
                :size="18"
              /> Host
            </button>
            <a
              class="btn--ghost btn--sm"
              :href="`/api/admin/bluff/packs/${item.id}/export`"
              download
            ><AppIcon
              name="download"
              :size="18"
            />Export</a>
            <button
              v-if="!item.archived"
              type="button"
              class="btn--danger btn--sm"
              :disabled="busy"
              @click="archivePack(item.id)"
            >
              <AppIcon
                name="archive"
                :size="18"
              /> Archive
            </button>
          </div>
        </article>
      </aside>

      <section
        v-if="pack"
        class="stack stack--lg"
      >
        <form
          class="stack"
          @submit.prevent="savePack"
        >
          <div class="input-wrapper">
            <label for="pack-name">Pack name</label>
            <div class="input-single">
              <AppIcon name="gamepad" />
              <input
                id="pack-name"
                v-model="pack.name"
                minlength="2"
                maxlength="120"
                required
              >
            </div>
          </div>
          <div class="input-wrapper">
            <label for="pack-description">Description</label>
            <div class="input-single input-single--area">
              <AppIcon name="message" />
              <textarea
                id="pack-description"
                v-model="pack.description"
                maxlength="1000"
              />
            </div>
          </div>
          <p class="fw-6">
            Default rules (the host can change them in the lobby)
          </p>
          <div class="score-grid">
            <label>Bluff timer (s)<span class="input-single input-single--soft"><input
              :value="pack.settings.bluffSeconds ?? ''"
              type="number"
              min="5"
              max="600"
              @input="pack.settings.bluffSeconds = timer(($event.target as HTMLInputElement).value)"
            ></span></label>
            <label>Vote timer (s)<span class="input-single input-single--soft"><input
              :value="pack.settings.voteSeconds ?? ''"
              type="number"
              min="5"
              max="600"
              @input="pack.settings.voteSeconds = timer(($event.target as HTMLInputElement).value)"
            ></span></label>
            <label>Truth points<span class="input-single input-single--soft"><input
              v-model.number="pack.settings.truthPoints"
              type="number"
              min="0"
              max="10000"
            ></span></label>
            <label>Points per team fooled<span class="input-single input-single--soft"><input
              v-model.number="pack.settings.foolPoints"
              type="number"
              min="0"
              max="10000"
            ></span></label>
          </div>
          <label class="check-row"><input
            v-model="pack.settings.profanityFilterEnabled"
            type="checkbox"
          > Filter profanity on the phones and the Big Screen</label>
          <div>
            <button
              type="submit"
              class="btn--primary"
              :disabled="busy || pack.archived"
            >
              <AppIcon name="check" /> Save pack
            </button>
          </div>
        </form>

        <h3 class="text-xl fw-6">
          Questions
        </h3>
        <div class="question-list">
          <details
            v-for="(question, index) in pack.questions"
            :key="question.id"
            class="question-editor"
          >
            <summary><span>{{ index + 1 }}. {{ question.prompt }}</span><strong>{{ question.active ? question.truth : 'inactive' }}</strong></summary>
            <form
              class="stack"
              @submit.prevent="saveQuestion(question.id!)"
            >
              <label class="stack stack--sm">Prompt<span class="input-single input-single--soft"><input
                v-model="drafts[question.id!]!.prompt"
                maxlength="300"
                required
              ></span></label>
              <label class="stack stack--sm">True answer<span class="input-single input-single--soft"><input
                v-model="drafts[question.id!]!.truth"
                maxlength="80"
                required
              ></span></label>
              <label class="stack stack--sm">Also accepted as the truth (comma separated)<span class="input-single input-single--soft"><input v-model="drafts[question.id!]!.aliases"></span></label>
              <label class="stack stack--sm">Explanation<span class="input-single input-single--area"><textarea
                v-model="drafts[question.id!]!.explanation"
                maxlength="1000"
                required
              /></span></label>
              <div class="score-grid">
                <label>Source name<span class="input-single input-single--soft"><input
                  v-model="drafts[question.id!]!.sourceName"
                  maxlength="200"
                  required
                ></span></label>
                <label>Source link (https)<span class="input-single input-single--soft"><input
                  v-model="drafts[question.id!]!.sourceUrl"
                  type="url"
                ></span></label>
              </div>
              <label class="stack stack--sm">Prepared decoys (comma separated)<span class="input-single input-single--soft"><input v-model="drafts[question.id!]!.decoys"></span></label>
              <label class="check-row"><input
                v-model="drafts[question.id!]!.active"
                type="checkbox"
              > Active</label>
              <div class="button-row">
                <button
                  type="submit"
                  class="btn--primary btn--sm"
                  :disabled="busy"
                >
                  Save question
                </button>
                <button
                  type="button"
                  class="btn--ghost btn--sm"
                  :disabled="busy || index === 0"
                  @click="move(index, -1)"
                >
                  Move up
                </button>
                <button
                  type="button"
                  class="btn--ghost btn--sm"
                  :disabled="busy || index === pack.questions.length - 1"
                  @click="move(index, 1)"
                >
                  Move down
                </button>
                <button
                  type="button"
                  class="btn--danger btn--sm"
                  :disabled="busy"
                  @click="deleteQuestion(question.id!)"
                >
                  Delete
                </button>
              </div>
            </form>
          </details>
        </div>

        <form
          class="stack panel panel--dashed"
          @submit.prevent="addQuestion"
        >
          <h3 class="text-xl fw-6">
            Add a question
          </h3>
          <label class="stack stack--sm">Prompt (an unusual fact or fill-in-the-blank)<span class="input-single input-single--soft"><input
            v-model="newQuestion.prompt"
            maxlength="300"
            placeholder="A group of flamingos is called a ________."
            required
          ></span></label>
          <label class="stack stack--sm">True answer<span class="input-single input-single--soft"><input
            v-model="newQuestion.truth"
            maxlength="80"
            required
          ></span></label>
          <label class="stack stack--sm">Also accepted as the truth (comma separated)<span class="input-single input-single--soft"><input v-model="newQuestion.aliases"></span></label>
          <label class="stack stack--sm">Explanation<span class="input-single input-single--area"><textarea
            v-model="newQuestion.explanation"
            maxlength="1000"
            required
          /></span></label>
          <div class="score-grid">
            <label>Source name<span class="input-single input-single--soft"><input
              v-model="newQuestion.sourceName"
              maxlength="200"
              required
            ></span></label>
            <label>Source link (https)<span class="input-single input-single--soft"><input
              v-model="newQuestion.sourceUrl"
              type="url"
            ></span></label>
          </div>
          <label class="stack stack--sm">Prepared decoys (comma separated)<span class="input-single input-single--soft"><input v-model="newQuestion.decoys"></span></label>
          <div>
            <button
              type="submit"
              class="btn--primary"
              :disabled="busy || pack.archived"
            >
              <AppIcon name="plus" /> Add question
            </button>
          </div>
        </form>
      </section>
    </div>
  </section>
</template>
